// How a visitor adds the site to their Home Screen decides what the help says,
// so the browser is named by what it can do, not by brand.
export type InstallPlatform =
  // Share, then Add to Home Screen.
  | 'ios-safari'
  // Chrome, Edge or Firefox on iOS: the same Share item, placed elsewhere.
  | 'ios-browser'
  // Instagram, Facebook, TikTok and other apps' built-in browsers, which have
  // no Add to Home Screen at all: the page has to be opened in a browser first.
  | 'in-app'
  // Chrome, Samsung Internet, Firefox and Edge on Android: the browser menu.
  | 'android'
  // Chrome or Edge on a computer: the install item in the browser menu.
  | 'desktop-chromium'
  // File, then Add to Dock.
  | 'mac-safari'
  | 'unsupported';

export type OfferPlatform = Exclude<InstallPlatform, 'unsupported'>;

export const INSTALL_ACTION = 'install';
export const INSTALL_PATH = `/?action=${INSTALL_ACTION}`;
export const APPLE_TOUCH_ICON_PATH = '/apple-touch-icon.png';

export const isPhonePlatform = (platform: InstallPlatform) =>
  platform === 'ios-safari' ||
  platform === 'ios-browser' ||
  platform === 'in-app' ||
  platform === 'android';

// Tokens the common in-app browsers put in their user agent. Heuristic by
// nature: an app that sends none reads as the browser it is built on.
const IN_APP_BROWSER =
  /Instagram|FBAN|FBAV|FB_IAB|FBIOS|musical_ly|BytedanceWebview|TikTok|Line\/|Snapchat|LinkedInApp|Pinterest|Twitter|GSA\//;

const OTHER_IOS_BROWSER = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/;

// iPadOS asks for desktop pages and names itself a Mac; only the touch points
// tell it apart.
export const installPlatformOf = (
  userAgent: string,
  maxTouchPoints: number
): InstallPlatform => {
  const ios =
    /iPhone|iPad|iPod/.test(userAgent) ||
    (/Macintosh/.test(userAgent) && maxTouchPoints > 1);
  if (IN_APP_BROWSER.test(userAgent)) return 'in-app';
  if (ios) {
    if (OTHER_IOS_BROWSER.test(userAgent)) return 'ios-browser';
    // A view an app builds on WebKit leaves Safari's token out.
    return /Safari\//.test(userAgent) ? 'ios-safari' : 'in-app';
  }
  if (/Android/.test(userAgent)) {
    // Android's WebView, which apps open links in, marks itself with "wv".
    return /; wv\)/.test(userAgent) ? 'in-app' : 'android';
  }
  if (/Chrome\/|Edg\//.test(userAgent) && !/OPR\//.test(userAgent)) {
    return 'desktop-chromium';
  }
  if (/Macintosh/.test(userAgent) && /Safari\//.test(userAgent)) {
    return 'mac-safari';
  }
  return 'unsupported';
};

// navigator.standalone is iOS's own flag, older than the media query and kept
// beside it for the cases the query has missed.
export const isStandaloneDisplay = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

export const installActionLabel = (platform: InstallPlatform) => {
  if (platform === 'desktop-chromium') return 'Install app';
  if (platform === 'mac-safari') return 'Add to Dock';
  return 'Add to Home Screen';
};
